import { Link } from "react-router-dom";
import { ShoppingBag, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const CartPage = () => {
  const { cart, cartTotal, updateCartItem, removeFromCart } = useApp();

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      const success = await removeFromCart(productId);
      if (success) toast.success("Item removed from cart");
    } else {
      await updateCartItem(productId, newQuantity);
    }
  };

  const handleRemove = async (productId, productName) => {
    const success = await removeFromCart(productId);
    if (success) {
      toast.success(`${productName} removed from cart`);
    }
  };

  const deliveryCharge = cartTotal >= 500 ? 0 : 50;
  const total = cartTotal + deliveryCharge;

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center">
          <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-12 h-12 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Your Cart is Empty</h2>
          <p className="text-muted-foreground mb-6">
            Looks like you haven't added anything to your cart yet.
          </p>
          <Link to="/categories">
            <Button size="lg" data-testid="start-shopping">
              Start Shopping
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="cart-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-8" data-testid="cart-title">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <div
                key={item.product_id}
                className="flex gap-4 p-4 bg-white rounded-xl border"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Image */}
                <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden bg-muted">
                    <img
                      src={item.product?.images?.[0] || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=200"}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Link>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.product_id}`}>
                    <h3 className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-2">
                      {item.product?.name}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-bold text-primary">
                      ₹{item.product?.price?.toLocaleString()}
                    </span>
                    {item.product?.original_price && (
                      <span className="text-sm text-muted-foreground line-through">
                        ₹{item.product.original_price.toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border rounded-lg">
                      <button
                        onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                        className="quantity-btn rounded-l-lg rounded-r-none border-0 border-r h-8 w-8"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-12 text-center font-medium text-sm" data-testid={`quantity-${item.product_id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                        className="quantity-btn rounded-r-lg rounded-l-none border-0 border-l h-8 w-8"
                        disabled={item.quantity >= (item.product?.stock || 10)}
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleRemove(item.product_id, item.product?.name)}
                      data-testid={`remove-${item.product_id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>

                {/* Item Total */}
                <div className="hidden sm:block text-right">
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="font-bold text-lg">
                    ₹{((item.product?.price || 0) * item.quantity).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-xl font-bold mb-6">Order Summary</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium" data-testid="subtotal">₹{cartTotal.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Delivery</span>
                  <span className="font-medium" data-testid="delivery-charge">
                    {deliveryCharge === 0 ? (
                      <span className="text-green-600">FREE</span>
                    ) : (
                      `₹${deliveryCharge}`
                    )}
                  </span>
                </div>

                {cartTotal < 500 && (
                  <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                    Add ₹{(500 - cartTotal).toLocaleString()} more for free delivery!
                  </p>
                )}

                <hr />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary" data-testid="total">₹{total.toLocaleString()}</span>
                </div>
              </div>

              <Link to="/checkout" className="block mt-6">
                <Button className="w-full btn-primary" size="lg" data-testid="checkout-button">
                  Proceed to Checkout
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>

              <Link to="/categories" className="block mt-4">
                <Button variant="outline" className="w-full" data-testid="continue-shopping">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
